// src/pages/Profile/components/EditProfileDialog.tsx
import React, { useState, useEffect } from 'react';
import {
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   TextField,
   Button,
   Grid,
   Typography,
   IconButton,
   CircularProgress,
   Box,
   Divider,
   InputLabel,
   Tooltip,
   FormHelperText,
   Card,
   CardContent,
   Chip,
   Avatar,
   Paper,
   useTheme,
   useMediaQuery,
} from '@mui/material';
import {
   Close as CloseIcon,
   Save as SaveIcon,
   LocationOn as LocationIcon,
   Facebook,
   Twitter,
   LinkedIn,
   Instagram,
   Work as WorkIcon,
   Business as BusinessIcon,
   Add as AddIcon,
   Delete as DeleteIcon,
   PhotoCamera as PhotoCameraIcon,
} from '@mui/icons-material';
import { doc, updateDoc, setDoc } from 'firebase/firestore';
import { db, storage } from '../../../firebase/config';
import { updateProfile } from 'firebase/auth';
import { auth } from '../../../firebase/config';
import { useDropzone } from 'react-dropzone';
import GooglePlacesSearch from '../../../components/common/GooglePlacesSearch';
import { uploadBytesResumable, getDownloadURL, uploadBytes, deleteObject } from 'firebase/storage';
import { ref } from 'firebase/storage';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

interface WhatIDoCard {
   id: string;
   title: string;
   description: string;
}

interface LookingForItem {
   id: string;
   title: string;
   description: string;
}

interface FeaturedProject {
   id: string;
   title: string;
   description: string;
}

interface Interest {
   id: string;
   emoji: string;
   name: string;
   selected: boolean;
   isCustom?: boolean;
}

interface EditProfileDialogProps {
   open: boolean;
   onClose: () => void;
   userId: string;
   profileData: {
      displayName?: string;
      email?: string;
      bio?: string;
      location?: string;
      title?: string;
      companyName?: string;
      website?: string;
      phoneNumber?: string;
      facebook?: string;
      twitter?: string;
      linkedin?: string;
      instagram?: string;
      tagline?: string;
      whatIDo?: WhatIDoCard[];
      interests?: Interest[];
      lookingFor?: LookingForItem[];
      featuredProjects?: FeaturedProject[];
      [key: string]: any;
   };
   onProfileUpdated: (updatedData: any) => void;
}

const EditProfileDialog: React.FC<EditProfileDialogProps> = ({
   open,
   onClose,
   userId,
   profileData,
   onProfileUpdated,
}) => {
   const theme = useTheme();
   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

   const [formData, setFormData] = useState({ ...profileData });
   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [profilePicture, setProfilePicture] = useState<any | null>(null);
   const [bioWordCount, setBioWordCount] = useState(0);
   const [taglineCount, setTaglineCount] = useState(0);
   const [fetchingData, setFetchingData] = useState(false);

   // New state for additional fields
   const [whatIDo, setWhatIDo] = useState<WhatIDoCard[]>(profileData.whatIDo || []);
   const [interests, setInterests] = useState<Interest[]>(profileData.interests || []);
   const [lookingFor, setLookingFor] = useState<LookingForItem[]>(profileData.lookingFor || []);
   const [featuredProjects, setFeaturedProjects] = useState<FeaturedProject[]>(
      profileData.featuredProjects || []
   );

   // State for showing add forms
   const [showWhatIDoForm, setShowWhatIDoForm] = useState(false);
   const [showLookingForForm, setShowLookingForForm] = useState(false);
   const [showFeaturedProjectsForm, setShowFeaturedProjectsForm] = useState(false);

   // Predefined interests
   const predefinedInterests: Interest[] = [
      { id: 'ai', emoji: '🤖', name: 'AI & Machine Learning', selected: false },
      { id: 'startup', emoji: '🚀', name: 'Startup Ecosystems', selected: false },
      { id: 'dev', emoji: '💻', name: 'Software Development', selected: false },
      { id: 'design', emoji: '📱', name: 'Product Design', selected: false },
      { id: 'data', emoji: '📊', name: 'Data Science', selected: false },
      { id: 'innovation', emoji: '💡', name: 'Innovation Strategy', selected: false },
      { id: 'creative', emoji: '🎨', name: 'Creative Technology', selected: false },
      { id: 'api', emoji: '🌐', name: 'API Development', selected: false },
   ];

   const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];

      if (file) {
         setProfilePicture(file);
      }
   };

   // Fetch fresh profile data from backend when dialog opens
   useEffect(() => {
      const fetchProfileData = async () => {
         if (!open || !userId) return;

         setFetchingData(true);
         try {
            const token = await auth.currentUser?.getIdToken();
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${userId}`, {
               headers: {
                  Authorization: `Bearer ${token}`,
               },
            });

            if (response.ok) {
               const freshData = await response.json();
               console.log('🔵 Fetched fresh profile data:', freshData);
               
               setFormData({ ...profileData, ...freshData });
               setBioWordCount(countWords(freshData.bio || ''));
               setTaglineCount((freshData.tagline || '').length);

      setWhatIDo(
                  (freshData.whatIDo || []).map((item: any) => ({
            ...item,
            description: item.description || '',
         }))
      );
               setInterests(freshData.interests || []);
      setLookingFor(
                  (freshData.lookingFor || []).map((item: any) => ({
            ...item,
            description: item.description || '',
         }))
      );
      setFeaturedProjects(
                  (freshData.featuredProjects || []).map((item: any) => ({
            ...item,
            description: item.description || '',
         }))
      );
            } else {
               // Fallback to prop data if fetch fails
               setFormData({ ...profileData });
               setBioWordCount(countWords(profileData.bio || ''));
               setTaglineCount((profileData.tagline || '').length);
               setWhatIDo((profileData.whatIDo || []).map((item: any) => ({
                  ...item,
                  description: item.description || '',
               })));
               setInterests(profileData.interests || []);
               setLookingFor((profileData.lookingFor || []).map((item: any) => ({
                  ...item,
                  description: item.description || '',
               })));
               setFeaturedProjects((profileData.featuredProjects || []).map((item: any) => ({
                  ...item,
                  description: item.description || '',
               })));
            }
         } catch (error) {
            console.error('Error fetching profile data:', error);
            // Fallback to prop data
            setFormData({ ...profileData });
         } finally {
            setFetchingData(false);
         }
      };

      fetchProfileData();
   }, [open, userId]);

   const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormData((prev) => ({
         ...prev,
         [name]: value,
      }));
   };

   const countWords = (text: string) => {
      // Remove HTML tags and count words
      const plainText = text.replace(/<[^>]*>/g, '').trim();
      return plainText === '' ? 0 : plainText.split(/\s+/).length;
   };

   const handleBioChange = (value: string) => {
      const wordCount = countWords(value);
      if (wordCount <= 500) {
         setFormData((prev) => ({
            ...prev,
            bio: value,
         }));
         setBioWordCount(wordCount);
      }
   };

   const handleTaglineChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      if (value.length <= 150) {
         setFormData((prev) => ({
            ...prev,
            tagline: value,
         }));
         setTaglineCount(value.length);
      }
   };

   // What I Do handlers
   const addWhatIDoCard = () => {
      if (whatIDo.length < 4) {
         setShowWhatIDoForm(true);
      }
   };

   const saveWhatIDoCard = (title: string, description: string) => {
         const newCard: WhatIDoCard = {
            id: Date.now().toString(),
         title,
         description,
         };
         setWhatIDo([...whatIDo, newCard]);
      setShowWhatIDoForm(false);
   };

   const cancelWhatIDoCard = () => {
      setShowWhatIDoForm(false);
   };

   const updateWhatIDoCard = (id: string, field: keyof WhatIDoCard, value: string) => {
      setWhatIDo((prev) =>
         prev.map((card) => (card.id === id ? { ...card, [field]: value } : card))
      );
   };

   const removeWhatIDoCard = (id: string) => {
      setWhatIDo((prev) => prev.filter((card) => card.id !== id));
   };

   // Looking For handlers
   const addLookingForItem = () => {
      if (lookingFor.length < 5) {
         setShowLookingForForm(true);
      }
   };

   const saveLookingForItem = (title: string, description: string) => {
         const newItem: LookingForItem = {
            id: Date.now().toString(),
         title,
         description,
         };
         setLookingFor([...lookingFor, newItem]);
      setShowLookingForForm(false);
   };

   const cancelLookingForItem = () => {
      setShowLookingForForm(false);
   };

   const updateLookingForItem = (id: string, field: keyof LookingForItem, value: string) => {
      setLookingFor((prev) =>
         prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
      );
   };

   const removeLookingForItem = (id: string) => {
      setLookingFor((prev) => prev.filter((item) => item.id !== id));
   };

   // Interest handlers
   const toggleInterest = (interestId: string) => {
      setInterests((prev) => {
         const existing = prev.find((i) => i.id === interestId);
         if (existing) {
            return prev.map((interest) =>
               interest.id === interestId ? { ...interest, selected: !interest.selected } : interest
            );
         } else {
            const predefined = predefinedInterests.find((i) => i.id === interestId);
            if (predefined) {
               return [...prev, { ...predefined, selected: true }];
            }
            return prev;
         }
      });
   };

   const addCustomInterest = () => {
      if (interests.length < 6) {
         const newInterest: Interest = {
            id: Date.now().toString(),
            emoji: '⭐',
            name: '',
            selected: true,
            isCustom: true,
         };
         setInterests([...interests, newInterest]);
      }
   };

   const updateCustomInterest = (id: string, field: keyof Interest, value: string) => {
      setInterests((prev) =>
         prev.map((interest) => (interest.id === id ? { ...interest, [field]: value } : interest))
      );
   };

   const removeCustomInterest = (id: string) => {
      setInterests((prev) => prev.filter((interest) => interest.id !== id));
   };

   // Featured Projects handlers
   const addFeaturedProject = () => {
      if (featuredProjects.length < 3) {
         setShowFeaturedProjectsForm(true);
      }
   };

   const saveFeaturedProject = (title: string, description: string) => {
         const newProject: FeaturedProject = {
            id: Date.now().toString(),
         title,
         description,
         };
         setFeaturedProjects([...featuredProjects, newProject]);
      setShowFeaturedProjectsForm(false);
   };

   const cancelFeaturedProject = () => {
      setShowFeaturedProjectsForm(false);
   };

   const updateFeaturedProject = (id: string, field: keyof FeaturedProject, value: string) => {
      setFeaturedProjects((prev) =>
         prev.map((project) => (project.id === id ? { ...project, [field]: value } : project))
      );
   };

   const removeFeaturedProject = (id: string) => {
      setFeaturedProjects((prev) => prev.filter((project) => project.id !== id));
   };

   // Helper function to compress and optimize image
   const compressImage = (file: File, maxWidth: number = 800, quality: number = 0.9): Promise<Blob> => {
      return new Promise((resolve, reject) => {
         const reader = new FileReader();
         reader.onload = (e) => {
            const img = new Image();
            img.onload = () => {
               const canvas = document.createElement('canvas');
               let { width, height } = img;

               // Maintain aspect ratio
               if (width > maxWidth) {
                  height = (height * maxWidth) / width;
                  width = maxWidth;
               }

               canvas.width = width;
               canvas.height = height;

               const ctx = canvas.getContext('2d');
               if (!ctx) {
                  reject(new Error('Failed to get canvas context'));
                  return;
               }

               // Use high-quality image smoothing
               ctx.imageSmoothingEnabled = true;
               ctx.imageSmoothingQuality = 'high';
               ctx.drawImage(img, 0, 0, width, height);

               canvas.toBlob(
                  (blob) => {
                     if (blob) {
                        resolve(blob);
                     } else {
                        reject(new Error('Failed to compress image'));
                     }
                  },
                  'image/jpeg',
                  quality
               );
            };
            img.onerror = () => reject(new Error('Failed to load image'));
            img.src = e.target?.result as string;
         };
         reader.onerror = () => reject(new Error('Failed to read file'));
         reader.readAsDataURL(file);
      });
   };

   const handleSubmit = async () => {
      console.log('🔵 handleSubmit called');
      setLoading(true);
      setError(null);

      try {
         console.log('🔵 Starting profile update...');

         if (auth.currentUser && formData.displayName !== profileData.displayName) {
            console.log('🔵 Updating Firebase Auth displayName...');
            await updateProfile(auth.currentUser, {
               displayName: formData.displayName,
            });
         }

         let profileUrl: any = '';

         if (profilePicture) {
            console.log('🔵 Compressing and uploading profile picture...');
            try {
               // Compress image before upload to maintain quality
               const compressedImage = await compressImage(profilePicture, 800, 0.92);
            const storageRef = ref(storage, `userProfiles/${userId}/profilePicture`);
               await uploadBytes(storageRef, compressedImage, {
                  contentType: 'image/jpeg',
                  cacheControl: 'public, max-age=31536000',
               });
            profileUrl = await getDownloadURL(storageRef);
            console.log('🔵 Profile picture uploaded:', profileUrl);
            } catch (compressError) {
               console.error('Error compressing image, uploading original:', compressError);
               // Fallback to original if compression fails
               const storageRef = ref(storage, `userProfiles/${userId}/profilePicture`);
               await uploadBytes(storageRef, profilePicture);
               profileUrl = await getDownloadURL(storageRef);
            }
         }

         // Strip HTML tags from bio to store as plain text
         const stripHtml = (html: string) => {
            if (!html) return '';
            return html.replace(/<[^>]*>/g, '').trim();
         };

         const updateData: any = {
            ...formData,
            bio: stripHtml(formData.bio || ''), // Strip HTML from bio
            whatIDo,
            interests,
            lookingFor,
            featuredProjects,
            lastUpdated: new Date(),
         };

         // Only update photoURL if a new profile picture was uploaded
         if (profileUrl) {
            updateData.photoURL = profileUrl;
         }

         console.log('🔵 Sending profile update to backend:', updateData);

         // Call backend API to update profile and sync across all collections
         const token = await auth.currentUser?.getIdToken();
         const response = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${userId}`, {
            method: 'PUT',
            headers: {
               'Content-Type': 'application/json',
               Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(updateData),
         });

         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update profile');
         }

         const result = await response.json();
         console.log('✅ Profile updated successfully:', result.message);

         // Fetch fresh profile data from backend after update
         try {
            const token = await auth.currentUser?.getIdToken();
            const freshDataResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/profile/${userId}`, {
               headers: {
                  Authorization: `Bearer ${token}`,
               },
            });

            if (freshDataResponse.ok) {
               const freshData = await freshDataResponse.json();
               console.log('✅ Fresh profile data fetched:', freshData);
               
               // Call onProfileUpdated with fresh data from backend
               onProfileUpdated({
                  ...freshData,
                  whatIDo,
                  interests,
                  lookingFor,
                  featuredProjects,
               });
            } else {
               // Fallback to local data if fetch fails
         onProfileUpdated({
            ...formData,
            ...profileData,
            whatIDo,
            interests,
            lookingFor,
            featuredProjects,
            photoURL: profileUrl || profileData.photoURL || '',
         });
            }
         } catch (fetchError) {
            console.error('Error fetching fresh data:', fetchError);
            // Fallback to local data
            onProfileUpdated({
               ...formData,
               ...profileData,
               whatIDo,
               interests,
               lookingFor,
               featuredProjects,
               photoURL: profileUrl || profileData.photoURL || '',
            });
         }
         
         console.log('🟢 Profile updated successfully!');

         onClose();
         setProfilePicture(null);
      } catch (err) {
         console.error('🔴 Error updating profile:', err);
         setError('Failed to update profile. Please try again.');
      } finally {
         setLoading(false);
      }
   };

   // Add form components
   const WhatIDoAddForm = () => {
      const [title, setTitle] = useState('');
      const [description, setDescription] = useState('');

      const handleSave = () => {
         if (title.trim()) {
            saveWhatIDoCard(title.trim(), description.trim());
            setTitle('');
            setDescription('');
         }
      };

      return (
         <Box
            sx={{
               p: 2,
               border: '2px dashed #00C4A7',
               borderRadius: 1.5,
               mb: 2,
            }}
         >
            <Typography variant='body2' fontWeight={500} color='#00C4A7' sx={{ mb: 2 }}>
               Add What You Do
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
               <TextField
                  fullWidth
                  size='small'
                  placeholder='What you do (e.g., Product Manager, Software Engineer)'
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  sx={{
                     '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                     },
                  }}
               />
               <TextField
                  fullWidth
                  size='small'
                  multiline
                  rows={2}
                  placeholder='Brief description of what you do...'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={{
                     '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                     },
                  }}
               />
               <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size='small' onClick={cancelWhatIDoCard} sx={{ color: '#6b7280' }}>
                     Cancel
                  </Button>
                  <Button
                     size='small'
                     variant='contained'
                     onClick={handleSave}
                     disabled={!title.trim()}
                     sx={{
                        background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                        '&:hover': {
                           background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                        },
                     }}
                  >
                     Save
                  </Button>
               </Box>
            </Box>
         </Box>
      );
   };

   const LookingForAddForm = () => {
      const [title, setTitle] = useState('');
      const [description, setDescription] = useState('');

      const handleSave = () => {
         if (title.trim()) {
            saveLookingForItem(title.trim(), description.trim());
            setTitle('');
            setDescription('');
         }
      };

      return (
         <Box
            sx={{
               p: 2,
               border: '2px dashed #00C4A7',
               borderRadius: 1.5,
               mb: 2,
            }}
         >
            <Typography variant='body2' fontWeight={500} color='#00C4A7' sx={{ mb: 2 }}>
               Add What You're Looking For
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
               <TextField
                  fullWidth
                  size='small'
                  placeholder='What you are looking for (e.g., Co-founder, Investment, Partnership)'
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  sx={{
                     '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                     },
                  }}
               />
               <TextField
                  fullWidth
                  size='small'
                  multiline
                  rows={2}
                  placeholder='Describe what you are looking for in more detail...'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={{
                     '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                     },
                  }}
               />
               <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size='small' onClick={cancelLookingForItem} sx={{ color: '#6b7280' }}>
                     Cancel
                  </Button>
                  <Button
                     size='small'
                     variant='contained'
                     onClick={handleSave}
                     disabled={!title.trim()}
                     sx={{
                        background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                        '&:hover': {
                           background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                        },
                     }}
                  >
                     Save
                  </Button>
               </Box>
            </Box>
         </Box>
      );
   };

   const FeaturedProjectsAddForm = () => {
      const [title, setTitle] = useState('');
      const [description, setDescription] = useState('');

      const handleSave = () => {
         if (title.trim()) {
            saveFeaturedProject(title.trim(), description.trim());
            setTitle('');
            setDescription('');
         }
      };

      return (
         <Box
            sx={{
               p: 2,
               border: '2px dashed #00C4A7',
               borderRadius: 1.5,
               mb: 2,
            }}
         >
            <Typography variant='body2' fontWeight={500} color='#00C4A7' sx={{ mb: 2 }}>
               Add Featured Project
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
               <TextField
                  fullWidth
                  size='small'
                  placeholder='Featured project title (e.g., Mobile App, Web Platform)'
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  sx={{
                     '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                     },
                  }}
               />
               <TextField
                  fullWidth
                  size='small'
                  multiline
                  rows={2}
                  placeholder='Describe your project, technologies used, and key achievements...'
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  sx={{
                     '& .MuiOutlinedInput-root': {
                        borderRadius: 1.5,
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                           borderColor: '#00C4A7',
                        },
                     },
                  }}
               />
               <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                  <Button size='small' onClick={cancelFeaturedProject} sx={{ color: '#6b7280' }}>
                     Cancel
                  </Button>
                  <Button
                     size='small'
                     variant='contained'
                     onClick={handleSave}
                     disabled={!title.trim()}
                     sx={{
                        background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                        '&:hover': {
                           background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                        },
                     }}
                  >
                     Save
                  </Button>
               </Box>
            </Box>
         </Box>
      );
   };

   return (
      <Dialog
         open={open}
         onClose={onClose}
         maxWidth='sm'
         fullWidth
         PaperProps={{
            sx: {
               borderRadius: 1.5,
               maxHeight: '90vh',
               overflow: 'hidden',
            },
         }}
      >
         <DialogTitle
            sx={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center',
               background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
               color: 'white',
               padding: 2,
            }}
         >
            <Typography variant='h6' fontWeight={500}>
               Edit Profile
            </Typography>
            <IconButton onClick={onClose} size='small' sx={{ color: 'white' }}>
               <CloseIcon />
            </IconButton>
         </DialogTitle>

         <DialogContent
            sx={{
               padding: 0,
               maxHeight: '80vh',
               overflowY: 'auto',
               '&::-webkit-scrollbar': {
                  width: '6px',
               },
               '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
               },
               '&::-webkit-scrollbar-thumb': {
                  background: '#00C4A7',
                  borderRadius: '1.5px',
               },
            }}
         >
            {fetchingData ? (
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'center',
                     minHeight: '300px',
                     flexDirection: 'column',
                     gap: 2,
                  }}
               >
                  <CircularProgress size={40} sx={{ color: '#00C4A7' }} />
                  <Typography color="text.secondary">Loading profile data...</Typography>
               </Box>
            ) : (
            <Box sx={{ padding: 2 }}>
               {/* Basic Info Section */}
               <Box sx={{ mb: 3 }}>
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid #e5e7eb',
                     }}
                  >
                     <Typography sx={{ fontSize: 16 }}>👤</Typography>
                     <Typography variant='subtitle1' fontWeight={500} color='#1f2937'>
                        Basic Information
                     </Typography>
                  </Box>

                  {/* Profile Photo */}
                  <Box sx={{ mb: 2 }}>
                     <Typography variant='body2' fontWeight={500} color='#374151' sx={{ mb: 1 }}>
                        Profile Photo
                     </Typography>
                     <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                           src={
                              profilePicture
                                 ? URL.createObjectURL(profilePicture)
                                 : profileData.photoURL
                           }
                           alt='Profile'
                           sx={{
                              width: 80,
                              height: 80,
                              background: theme.palette.primary.main,
                              fontSize: 28,
                              fontWeight: 'bold',
                           }}
                        >
                           {formData.displayName?.charAt(0) || 'U'}
                        </Avatar>
                        <Button
                           variant='contained'
                           component='label'
                           size='small'
                           sx={{
                              background: theme.palette.primary.main,
                              borderRadius: 1.5,
                              '&:hover': {
                                 background: theme.palette.primary.light,
                              },
                           }}
                        >
                           <PhotoCameraIcon sx={{ mr: 1 }} />
                           Upload Photo
                           <input type='file' accept='image/*' hidden onChange={handleFileChange} />
                        </Button>
                     </Box>
                     <Typography
                        variant='caption'
                        color='#6b7280'
                        sx={{ mt: 0.5, display: 'block' }}
                     >
                        Square image, at least 400x400px
                     </Typography>
                  </Box>

                  {/* Full Name */}
                  <Box sx={{ mb: 2 }}>
                     <Typography variant='body2' fontWeight={500} color='#374151' sx={{ mb: 1 }}>
                        Full Name
                     </Typography>
                     <TextField
                        fullWidth
                        name='displayName'
                        value={formData.displayName || ''}
                        onChange={handleChange}
                        placeholder='Your full name'
                        variant='outlined'
                        sx={{
                           '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                 borderColor: '#00C4A7',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                 borderColor: '#00C4A7',
                              },
                           },
                        }}
                     />
                  </Box>

                  {/* Tagline */}
                  <Box sx={{ mb: 2 }}>
                     <Typography variant='body2' fontWeight={500} color='#374151' sx={{ mb: 1 }}>
                        Tagline
                     </Typography>
                     <TextField
                        fullWidth
                        name='tagline'
                        value={formData.tagline || ''}
                        onChange={handleTaglineChange}
                        placeholder='What do you do in one sentence?'
                        variant='outlined'
                        sx={{
                           '& .MuiOutlinedInput-root': {
                              borderRadius: 1.5,
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                 borderColor: '#00C4A7',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                 borderColor: '#00C4A7',
                              },
                           },
                        }}
                     />
                     <Typography
                        variant='caption'
                        color='#9ca3af'
                        sx={{ textAlign: 'right', display: 'block', mt: 0.5 }}
                     >
                        {taglineCount}/150 characters
                     </Typography>
                  </Box>

                  {/* Location and Company */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                     <Box>
                        <Typography variant='body2' fontWeight={500} color='#374151' sx={{ mb: 1 }}>
                           Location
                        </Typography>
                        <GooglePlacesSearch
                           value={formData?.location || ''}
                           searchType='places'
                           label='Location'
                           placeholder='City, State'
                           onPlaceSelect={(place_name: any, lat: any, lng: any) => {
                              setFormData((prev) => ({
                                 ...prev,
                                 location: place_name,
                              }));
                           }}
                           sx={{
                              '& .MuiOutlinedInput-root': {
                                 borderRadius: 1.5,
                                 '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C4A7',
                                 },
                                 '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C4A7',
                                 },
                              },
                           }}
                        />
                     </Box>
                     <Box>
                        <Typography variant='body2' fontWeight={500} color='#374151' sx={{ mb: 1 }}>
                           Company
                        </Typography>
                        <TextField
                           fullWidth
                           name='companyName'
                           value={formData.companyName || ''}
                           onChange={handleChange}
                           placeholder='Your company'
                           variant='outlined'
                           sx={{
                              '& .MuiOutlinedInput-root': {
                                 borderRadius: 1.5,
                                 '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C4A7',
                                 },
                                 '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C4A7',
                                 },
                              },
                           }}
                        />
                     </Box>
                  </Box>
               </Box>

               {/* About Section */}
               <Box sx={{ mb: 3 }}>
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid #e5e7eb',
                     }}
                  >
                     <Typography sx={{ fontSize: 16 }}>📝</Typography>
                     <Typography variant='subtitle1' fontWeight={500} color='#1f2937'>
                        About
                     </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                     <Typography variant='body2' fontWeight={500} color='#374151' sx={{ mb: 1 }}>
                        Bio
                     </Typography>
                     <ReactQuill
                        value={formData.bio || ''}
                        onChange={handleBioChange}
                        placeholder="Tell people about yourself, what you're working on, and what you're passionate about..."
                        style={{ height: '80px', marginBottom: '30px' }}
                        modules={{
                           toolbar: [
                              ['bold', 'italic', 'underline'],
                              [{ list: 'ordered' }, { list: 'bullet' }],
                              ['link'],
                           ],
                        }}
                     />
                     <Typography
                        variant='caption'
                        color='#9ca3af'
                        sx={{ textAlign: 'right', display: 'block', mt: 0.5 }}
                     >
                        {bioWordCount}/500 characters
                     </Typography>
                  </Box>
               </Box>

               {/* What I Do Section */}
               <Box sx={{ mb: 3 }}>
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid #e5e7eb',
                     }}
                  >
                     <Typography sx={{ fontSize: 16 }}>💼</Typography>
                     <Typography variant='subtitle1' fontWeight={500} color='#1f2937'>
                        What I Do
                     </Typography>
                  </Box>
                  <Typography variant='body2' color='#6b7280' sx={{ mb: 2 }}>
                     Add up to 4 cards showcasing your expertise (Recommended)
                  </Typography>

                  {/* Show add form when adding new item */}
                  {showWhatIDoForm && <WhatIDoAddForm />}

                  {/* Show existing items as read-only cards */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                     {whatIDo.map((card) => (
                        <Box
                           key={card.id}
                           sx={{
                              p: 2,
                              border: '1px solid #e2e8f0',
                              borderRadius: 1.5,
                              position: 'relative',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'flex-start',
                              }}
                           >
                              <Box sx={{ flex: 1, pr: 1 }}>
                                 <Typography
                                    variant='body1'
                                    fontWeight={500}
                                    color='#1f2937'
                                    sx={{ mb: 0.5 }}
                                 >
                                    {card.title}
                                 </Typography>
                                 {card.description && (
                                    <Typography variant='body2' color='#6b7280'>
                                       {card.description}
                                    </Typography>
                                 )}
                              </Box>
                              <IconButton
                                 onClick={() => removeWhatIDoCard(card.id)}
                                 size='small'
                                 sx={{
                                    width: 32,
                                    height: 32,
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    borderRadius: 1,
                                    '&:hover': {
                                       background: '#fecaca',
                                    },
                                 }}
                              >
                                 <DeleteIcon fontSize='small' />
                              </IconButton>
                           </Box>
                        </Box>
                     ))}
                  </Box>

                  {whatIDo.length < 4 && !showWhatIDoForm && (
                     <Button
                        fullWidth
                        variant='outlined'
                        onClick={addWhatIDoCard}
                        sx={{
                           border: '1px dashed #d1d5db',
                           borderRadius: 1.5,
                           color: '#00C4A7',
                           fontWeight: 500,
                           py: 1,
                           '&:hover': {
                              background: '#f3f4f6',
                              borderColor: '#00C4A7',
                           },
                        }}
                     >
                        + Add What You Do ({whatIDo.length}/4)
                     </Button>
                  )}
               </Box>

               {/* Interests Section */}
               <Box sx={{ mb: 3 }}>
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid #e5e7eb',
                     }}
                  >
                     <Typography sx={{ fontSize: 16 }}>🎯</Typography>
                     <Typography variant='subtitle1' fontWeight={500} color='#1f2937'>
                        Interests & Expertise
                     </Typography>
                  </Box>
                  <Typography variant='body2' color='#6b7280' sx={{ mb: 2 }}>
                     Select up to 6 interests (tap to select/deselect)
                  </Typography>

                  <Grid container spacing={1.5} sx={{ mb: 2 }}>
                     {predefinedInterests.map((interest) => {
                        const isSelected =
                           interests.find((i) => i.id === interest.id)?.selected || false;
                        return (
                           <Grid item xs={6} sm={4} key={interest.id}>
                              <Card
                                 onClick={() => toggleInterest(interest.id)}
                                 sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    background: isSelected
                                       ? 'linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)'
                                       : '#f9fafb',
                                    border: isSelected ? '2px solid #00C4A7' : '2px solid #e5e7eb',
                                    borderRadius: 1.5,
                                    '&:hover': {
                                       borderColor: '#00C4A7',
                                    },
                                 }}
                              >
                                 <Typography sx={{ fontSize: 32, mb: 1 }}>
                                    {interest.emoji}
                                 </Typography>
                                 <Typography variant='body2' fontWeight={600} color='#374151'>
                                    {interest.name}
                                 </Typography>
                              </Card>
                           </Grid>
                        );
                     })}
                  </Grid>

                  {/* Custom Interests */}
                  {interests
                     .filter((interest) => interest.isCustom)
                     .map((interest) => (
                        <Box key={interest.id} sx={{ mb: 2 }}>
                           <Box
                              sx={{
                                 p: 2,
                                 background: 'white',
                                 border: '1px solid #e5e7eb',
                                 borderRadius: 1.5,
                              }}
                           >
                              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                                 <TextField
                                    fullWidth
                                    size='small'
                                    placeholder='Custom interest (e.g., Photography, Cooking, Travel)'
                                    value={interest.name}
                                    onChange={(e) =>
                                       updateCustomInterest(interest.id, 'name', e.target.value)
                                    }
                                    sx={{
                                       '& .MuiOutlinedInput-root': {
                                          borderRadius: 1.5,
                                          '&:hover .MuiOutlinedInput-notchedOutline': {
                                             borderColor: '#00C4A7',
                                          },
                                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                             borderColor: '#00C4A7',
                                          },
                                       },
                                    }}
                                 />
                                 <IconButton
                                    onClick={() => removeCustomInterest(interest.id)}
                                    sx={{
                                       width: 36,
                                       height: 36,
                                       background: '#fee2e2',
                                       color: '#dc2626',
                                       borderRadius: 1.5,
                                       '&:hover': {
                                          background: '#fecaca',
                                       },
                                    }}
                                 >
                                    <DeleteIcon />
                                 </IconButton>
                              </Box>
                           </Box>
                        </Box>
                     ))}

                  {interests.length < 6 && (
                     <Button
                        fullWidth
                        variant='outlined'
                        onClick={addCustomInterest}
                        sx={{
                           border: '1px dashed #d1d5db',
                           borderRadius: 1.5,
                           color: '#00C4A7',
                           fontWeight: 500,
                           py: 1,
                           '&:hover': {
                              background: '#f3f4f6',
                              borderColor: '#00C4A7',
                           },
                        }}
                     >
                        + Add Custom Interest ({interests.length}/6)
                     </Button>
                  )}
               </Box>

               {/* Looking For Section */}
               <Box sx={{ mb: 3 }}>
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid #e5e7eb',
                     }}
                  >
                     <Typography sx={{ fontSize: 16 }}>🔍</Typography>
                     <Typography variant='subtitle1' fontWeight={500} color='#1f2937'>
                        Looking For
                     </Typography>
                  </Box>
                  <Typography variant='body2' color='#6b7280' sx={{ mb: 2 }}>
                     Add up to 5 things you&apos;re seeking (partnerships, opportunities, etc.)
                  </Typography>

                  {/* Show add form when adding new item */}
                  {showLookingForForm && <LookingForAddForm />}

                  {/* Show existing items as read-only cards */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                     {lookingFor.map((item) => (
                        <Box
                           key={item.id}
                           sx={{
                              p: 2,
                              border: '1px solid #e2e8f0',
                              borderRadius: 1.5,
                              position: 'relative',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'flex-start',
                              }}
                           >
                              <Box sx={{ flex: 1, pr: 1 }}>
                                 <Typography
                                    variant='body1'
                                    fontWeight={500}
                                    color='#1f2937'
                                    sx={{ mb: 0.5 }}
                                 >
                                    {item.title}
                                 </Typography>
                                 {item.description && (
                                    <Typography variant='body2' color='#6b7280'>
                                       {item.description}
                                    </Typography>
                                 )}
                              </Box>
                              <IconButton
                                 onClick={() => removeLookingForItem(item.id)}
                                 size='small'
                                 sx={{
                                    width: 32,
                                    height: 32,
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    borderRadius: 1,
                                    '&:hover': {
                                       background: '#fecaca',
                                    },
                                 }}
                              >
                                 <DeleteIcon fontSize='small' />
                              </IconButton>
                           </Box>
                        </Box>
                     ))}
                  </Box>

                  {lookingFor.length < 5 && !showLookingForForm && (
                     <Button
                        fullWidth
                        variant='outlined'
                        onClick={addLookingForItem}
                        sx={{
                           border: '1px dashed #d1d5db',
                           borderRadius: 1.5,
                           color: '#00C4A7',
                           fontWeight: 500,
                           py: 1,
                           '&:hover': {
                              background: '#f3f4f6',
                              borderColor: '#00C4A7',
                           },
                        }}
                     >
                        + Add What You're Looking For ({lookingFor.length}/5)
                     </Button>
                  )}
               </Box>

               {/* Featured Projects Section */}
               <Box sx={{ mb: 3 }}>
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid #e5e7eb',
                     }}
                  >
                     <Typography sx={{ fontSize: 16 }}>⭐</Typography>
                     <Typography variant='subtitle1' fontWeight={500} color='#1f2937'>
                        Featured Projects
                     </Typography>
                  </Box>

                  <Typography variant='body2' color='#6b7280' sx={{ mb: 2 }}>
                     Add up to 3 featured projects
                  </Typography>

                  {/* Show add form when adding new item */}
                  {showFeaturedProjectsForm && <FeaturedProjectsAddForm />}

                  {/* Show existing items as read-only cards */}
                  {featuredProjects.map((project) => (
                     <Box key={project.id} sx={{ mb: 2 }}>
                        <Box
                           sx={{
                              p: 2,
                              border: '1px solid #e2e8f0',
                              borderRadius: 1.5,
                              position: 'relative',
                           }}
                        >
                           <Box
                              sx={{
                                 display: 'flex',
                                 justifyContent: 'space-between',
                                 alignItems: 'flex-start',
                              }}
                           >
                              <Box sx={{ flex: 1, pr: 1 }}>
                                 <Typography
                                    variant='body1'
                                    fontWeight={500}
                                    color='#1f2937'
                                    sx={{ mb: 0.5 }}
                                 >
                                    {project.title}
                                 </Typography>
                                 {project.description && (
                                    <Typography variant='body2' color='#6b7280'>
                                       {project.description}
                                    </Typography>
                                 )}
                              </Box>
                              <IconButton
                                 onClick={() => removeFeaturedProject(project.id)}
                                 size='small'
                                 sx={{
                                    width: 32,
                                    height: 32,
                                    background: '#fee2e2',
                                    color: '#dc2626',
                                    borderRadius: 1,
                                    '&:hover': {
                                       background: '#fecaca',
                                    },
                                 }}
                              >
                                 <DeleteIcon fontSize='small' />
                              </IconButton>
                           </Box>
                        </Box>
                     </Box>
                  ))}

                  {featuredProjects.length < 3 && !showFeaturedProjectsForm && (
                     <Button
                        fullWidth
                        variant='outlined'
                        onClick={addFeaturedProject}
                        sx={{
                           border: '1px dashed #d1d5db',
                           borderRadius: 1.5,
                           color: '#00C4A7',
                           fontWeight: 500,
                           py: 1,
                           '&:hover': {
                              background: '#f3f4f6',
                              borderColor: '#00C4A7',
                           },
                        }}
                     >
                        + Add Featured Project ({featuredProjects.length}/3)
                     </Button>
                  )}
               </Box>

               {/* Contact Section */}
               <Box sx={{ mb: 2 }}>
                  <Box
                     sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1.5,
                        pb: 1,
                        borderBottom: '1px solid #e5e7eb',
                     }}
                  >
                     <Typography sx={{ fontSize: 16 }}>📞</Typography>
                     <Typography variant='subtitle1' fontWeight={500} color='#1f2937'>
                        Contact Information
                     </Typography>
                  </Box>

                  <Grid container spacing={2}>
                     <Grid item xs={12}>
                        <Typography variant='body2' fontWeight={500} color='#374151' sx={{ mb: 1 }}>
                           Email
                        </Typography>
                        <TextField
                           fullWidth
                           name='email'
                           value={formData.email || ''}
                           onChange={handleChange}
                           placeholder='your@email.com'
                           variant='outlined'
                           disabled
                           sx={{
                              '& .MuiOutlinedInput-root': {
                                 borderRadius: 1.5,
                              },
                           }}
                        />
                     </Grid>
                     <Grid item xs={12}>
                        <Typography variant='body2' fontWeight={500} color='#374151' sx={{ mb: 1 }}>
                           Website
                        </Typography>
                        <TextField
                           fullWidth
                           name='website'
                           value={formData.website || ''}
                           onChange={handleChange}
                           placeholder='www.yoursite.com'
                           variant='outlined'
                           sx={{
                              '& .MuiOutlinedInput-root': {
                                 borderRadius: 1.5,
                                 '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C4A7',
                                 },
                                 '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C4A7',
                                 },
                              },
                           }}
                        />
                     </Grid>
                     <Grid item xs={12}>
                        <Typography variant='body2' fontWeight={500} color='#374151' sx={{ mb: 1 }}>
                           Phone Number
                        </Typography>
                        <TextField
                           fullWidth
                           name='phoneNumber'
                           value={formData.phoneNumber || ''}
                           onChange={handleChange}
                           placeholder='+1 (555) 123-4567'
                           variant='outlined'
                           type='tel'
                           sx={{
                              '& .MuiOutlinedInput-root': {
                                 borderRadius: 1.5,
                                 '&:hover .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C4A7',
                                 },
                                 '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: '#00C4A7',
                                 },
                              },
                           }}
                        />
                     </Grid>
                  </Grid>
               </Box>

               {error && (
                  <Box sx={{ mt: 2 }}>
                     <Typography color='error'>{error}</Typography>
                  </Box>
               )}

               {/* Action Buttons - Now part of scrollable content */}
               <Box
                  sx={{
                     mt: 4,
                     p: 2,
                     display: 'flex',
                     justifyContent: 'flex-end',
                     gap: 1,
                  }}
               >
                  <Button
                     onClick={onClose}
                     disabled={loading}
                     variant='outlined'
                     sx={{
                        color: '#6b7280',
                        borderColor: '#e5e7eb',
                        '&:hover': {
                           background: '#f3f4f6',
                           borderColor: '#d1d5db',
                        },
                     }}
                  >
                     Cancel
                  </Button>
                  <Button
                     variant='contained'
                     onClick={handleSubmit}
                     disabled={loading}
                     startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                     sx={{
                        background: 'linear-gradient(135deg, #00C4A7 0%, #004369 100%)',
                        '&:hover': {
                           background: 'linear-gradient(135deg, #007075 0%, #002A42 100%)',
                        },
                     }}
                  >
                     {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
               </Box>
            </Box>
            )}
         </DialogContent>
      </Dialog>
   );
};

export default EditProfileDialog;